import { assignCommonProperties } from '../util/data/assign-common-properties.js'

import { jsonProperty, jsonClass } from '@sumbricht/json-class-serializer'
import type {
	BusinessObjectTypeType,
	ModuleIdType,
	BusinessObjectIdType,
	OnlyData,
} from '../types.js'

import { type BusinessObject } from './business-object.js'
import { hasBoInfo, isBo } from '../consts.js'
@jsonClass('BoReference')
export class BoReference<
	BoType extends BusinessObjectTypeType = BusinessObjectTypeType,
> {
	@jsonProperty(String)
	moduleId!: ModuleIdType
	@jsonProperty(String)
	boId!: BusinessObjectIdType
	@jsonProperty(String)
	boType!: BoType

	constructor(init?: OnlyData<BoReference>) {
		assignCommonProperties(this, init)
	}

	equals(other: any) {
		if (!(other instanceof BoReference)) return false
		return this.matches(other)
	}

	matches(
		other:
			| {
					moduleId: ModuleIdType
					boType: BusinessObjectTypeType
					boId: BusinessObjectIdType
			  }
			| BusinessObject,
	) {
		if (isBo(other)) {
			return (
				this.moduleId == other.moduleId &&
				this.boId == other.boId &&
				this.boType == other.__type
			)
		}
		return (
			this.moduleId == other?.moduleId &&
			this.boId == other?.boId &&
			this.boType == other?.boType
		)
	}

	isNonEmpty() {
		return this.moduleId && this.boId
	}

	getQualifiedName(subType?: string) {
		if (!this.isNonEmpty()) return ''
		return `${this.moduleId}.${this.boId}${subType ? `.${subType}` : ''}`
	}

	getQualifiedTypeAndName() {
		if (!this.isNonEmpty()) return ''
		return `${this.boType}:${this.moduleId}.${this.boId}`
	}

	isFullyDefined() {
		return this.moduleId && this.boId && this.boType
	}

	copyFrom(otherRef: BoReference<BoType>) {
		this.boType = otherRef.boType
		this.moduleId = otherRef.moduleId
		this.boId = otherRef.boId
	}

	forModule(contextModule: ModuleIdType | BusinessObject | BoReference) {
		if (this.moduleId != 'This') return this
		if (contextModule instanceof BoReference)
			contextModule = contextModule.moduleId
		if (isBo(contextModule)) contextModule = contextModule.moduleId

		return new BoReference<BoType>({
			...this,
			moduleId: contextModule,
		})
	}

	static fromQualifiedName<BoType extends BusinessObjectTypeType>(
		qualifiedName: string,
		boType: BoType,
	) {
		const parts = qualifiedName?.split('.') ?? []
		const moduleId = parts.splice(0, 1)[0]
		const boId = parts.join('.')

		return new this<BoType>({
			boType,
			moduleId,
			boId,
		})
	}

	static fromQualifiedTypeAndName(qualifiedTypeAndName: string) {
		const [boType, qualifiedName] = qualifiedTypeAndName?.split(':') ?? []
		return this.fromQualifiedName(
			qualifiedName,
			boType as BusinessObjectTypeType,
		)
	}
}
